import { createStore } from './createStore';

const initialState = {
  projectId: null,
  projectInternalId: null,
  projectName: 'Untitled Project',
  metadata: {
    title: null,
    style: null,
    motionGraphicsStyle: null,
    aspectRatio: null,
    length: null,
    voiceover: null,
  },
  status: null,
  // Project engine format: 'legacy' (relational timeline) | 'hyperframes'
  // (composition workspace). Drives which editor surface EditorPage mounts.
  format: 'legacy',
  script: '',
  isScriptComplete: false,
  renderedVideoUrl: null,
  audioClips: [], // Project-level audio clips with videoId
};

/**
 * Project-level metadata (name, style, status, script, ids).
 */
export const useProjectStore = createStore('ProjectStore', (set, get) => ({
  ...initialState,

  /**
   * Set the public project identifier (slug).
   * @param {string|null} projectId
   */
  setProjectId: (projectId) => set({ projectId }),

  /**
   * Set the internal UUID used for Supabase filters.
   * @param {string|null} projectInternalId
   */
  setProjectInternalId: (projectInternalId) => set({ projectInternalId }),

  /**
   * Update the project name displayed in the UI.
   * @param {string} projectName
   */
  setProjectName: (projectName) => set({ projectName }),

  /**
   * Replace the metadata payload entirely.
   * @param {object} metadata
   */
  setMetadata: (metadata) =>
    set(() => ({
      metadata: {
        title: metadata?.title ?? null,
        style: metadata?.style ?? null,
        motionGraphicsStyle: metadata?.motionGraphicsStyle ?? null,
        aspectRatio: metadata?.aspectRatio ?? null,
        length: metadata?.length ?? null,
        voiceover: metadata?.voiceover ?? null,
      },
    })),

  /**
   * Merge metadata fields.
   * @param {Partial<object>} updates
   */
  updateMetadata: (updates) =>
    set((state) => ({
      metadata: {
        ...state.metadata,
        ...updates,
      },
    })),

  /**
   * Update the project status ('processing', 'completed', etc.).
   * @param {string|null} status
   */
  setStatus: (status) => set({ status }),

  /**
   * Set the project engine format ('legacy' | 'hyperframes').
   * @param {string|null} format
   */
  setFormat: (format) => set({ format: format || 'legacy' }),

  /**
   * Set the latest script content.
   * @param {string} script
   * @param {boolean} [isComplete]
   */
  setScript: (script, isComplete = true) =>
    set({
      script,
      isScriptComplete: isComplete,
    }),

  /**
   * Cache the rendered full-project video URL.
   * @param {string|null} renderedVideoUrl
   */
  setRenderedVideoUrl: (renderedVideoUrl) => set({ renderedVideoUrl: renderedVideoUrl || null }),

  /**
   * Set project-level audio clips (with videoId populated).
   * @param {Array} audioClips
   */
  setAudioClips: (audioClips) => set({ audioClips: audioClips || [] }),

  /**
   * Update a single audio clip in the project-level array.
   * @param {string} clipId
   * @param {object|function} updates - Object to merge or updater function
   */
  updateAudioClip: (clipId, updates) =>
    set((state) => ({
      audioClips: state.audioClips.map((clip) => {
        if (clip.id !== clipId) return clip;
        if (typeof updates === 'function') {
          return updates(clip);
        }
        return { ...clip, ...updates };
      }),
    })),

  /**
   * Add a new audio clip to the project-level array.
   * @param {object} audioClip
   */
  addAudioClip: (audioClip) =>
    set((state) => {
      // Avoid duplicates
      if (state.audioClips.some((c) => c.id === audioClip.id)) {
        return state;
      }
      return { audioClips: [...state.audioClips, audioClip] };
    }),

  /**
   * Insert or merge a clip by id.
   * @param {object} audioClip
   */
  upsertAudioClip: (audioClip) =>
    set((state) => {
      if (!audioClip?.id) return state;
      const existingIndex = state.audioClips.findIndex((c) => c.id === audioClip.id);
      if (existingIndex === -1) {
        return { audioClips: [...state.audioClips, audioClip] };
      }
      const updated = [...state.audioClips];
      updated[existingIndex] = { ...updated[existingIndex], ...audioClip };
      return { audioClips: updated };
    }),

  /**
   * Remove an audio clip from the project-level array.
   * @param {string} clipId
   */
  removeAudioClip: (clipId) =>
    set((state) => ({
      audioClips: state.audioClips.filter((c) => c.id !== clipId),
    })),

  /**
   * Lookup helpers for canonical audio clip state.
   */
  getAudioClipById: (clipId) => {
    if (!clipId) return null;
    return get().audioClips.find((clip) => clip.id === clipId) || null;
  },
  getAudioClipsByVideoId: (videoId) => {
    if (!videoId) return [];
    return get().audioClips.filter((clip) => clip.videoId === videoId);
  },

  /**
   * Reset store to the initial state.
   */
  resetProject: () => set(() => initialState),
}));


